import { Button, Card, CardBody, DatePicker, TimeInput } from '@heroui/react';
import { parseDate, Time } from '@internationalized/date';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import useSegment from '@/hooks/useSegment';
import {
  surveyHelperContext,
  reviewHelperContext,
} from '@/patient-app-common/helpers/survey_helper';
import { usePostTrackerMutation } from '@/patient-app-common/api/trackerApi';

import { QuestionType } from './questions/QuestionType';
import { SurveyLoader } from './layout/SurveyLoader';

const OPEN_EVENT = 'Survey Opened';
const CLOSE_EVENT = 'Survey Closed';
const SUBMIT_SUCCESS_EVENT = 'Survey Completed';
const SUBMIT_FAIL_EVENT = 'Survey failed to submit';

export function SurveyFlat({
  eventId,
  idempotencyKey,
  patient,
  patientDatesInMS,
  surveyData,
}) {
  const { t } = useTranslation('survey');
  const navigate = useNavigate();

  const { trackSurvey } = useSegment();

  const [submitTracker] = usePostTrackerMutation();
  const submittingRef = useRef(false);

  const { medicineHistory } = patient;

  const { questions } = surveyData;

  const [date, setDate] = useState(new Date());
  const [hour, setHour] = useState(dayjs().format('H'));
  const [minute, setMinute] = useState(dayjs().format('mm'));
  const [answers, setAnswers] = useState({});

  const [loader, setLoader] = useState(null);

  const [surveyHelper, setSurveyHelper] = useState(null);
  const [reviewHelper, setReviewHelper] = useState(null);
  const [errorFields, setErrorFields] = useState([]);

  useEffect(() => {
    trackSurvey(OPEN_EVENT, { id: eventId }, surveyData);
  }, []);

  useEffect(() => {
    const helper = surveyHelperContext({
      patientDatesInMS,
      surveyData,
      editMode: false,
    });
    setSurveyHelper(helper);
  }, [patientDatesInMS, surveyData]);

  useEffect(() => {
    const helper = reviewHelperContext({
      surveyData,
      answers,
      profile: {},
      medicineHistory,
    });
    setReviewHelper(helper);
  }, [answers, medicineHistory]);

  const getAnswer = (value, index) => {
    const answersClone = { ...answers };
    const questionID = questions[index].id;
    answersClone[questionID] = value;
    setAnswers(answersClone);
  };

  const handleExit = async () => {
    trackSurvey(CLOSE_EVENT, { id: eventId }, surveyData);
    return navigate(-1);
  };

  const getDateTime = () => {
    const missing = [];

    const now = dayjs().valueOf();

    if (!date || !dayjs(date).isValid()) {
      missing.push('date');
    }

    if (_.isNil(hour) || _.isNil(minute)) {
      missing.push('time');
    }

    const datetime = dayjs(date).hour(hour).minute(minute);

    if (datetime.valueOf() > now) {
      missing.push('date', 'time');
    }

    if (missing.length) {
      setErrorFields([...errorFields, ...missing]);
      return false;
    }

    return datetime.valueOf();
  };

  const validateAnswers = () => {
    const missing = [];

    questions.forEach((question, index) => {
      const questionType = questions[index].type;
      const answer = answers[questions[index].id];
      const valid = surveyHelper.validateAnswer(questionType, answer);

      if (!valid) {
        missing.push(index);
      }
    });

    setErrorFields(missing);
    return _.isEmpty(missing);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    const valid = validateAnswers();

    if (!valid) return;

    let datetime = null;
    if (surveyData.metricTracker) {
      datetime = getDateTime();
      if (!datetime) return;
    }

    submittingRef.current = true;
    setLoader({ text: t('review.loading_text') });
    let params = reviewHelper.createSurveyParams(
      'metric_event',
      eventId,
      datetime
    );

    const photoParams = reviewHelper.createPhotoParams();
    if (photoParams && Object.keys(photoParams).length > 0) {
      params = { ...params, ...photoParams };
    }
    params.idempotencyKey = idempotencyKey.current;

    try {
      const result = await submitTracker(params).unwrap();
      if (result.submitted) {
        trackSurvey(SUBMIT_SUCCESS_EVENT, { id: eventId }, surveyData);
        setLoader(null);

        navigate(`/recommendation/${surveyData.id}`, {
          state: { surveyData },
        });
      } else {
        submittingRef.current = false;
        trackSurvey(SUBMIT_FAIL_EVENT, { id: eventId }, surveyData);
        setLoader({
          text: t('common.error.server'),
          type: 'error',
        });
      }
    } catch (e) {
      submittingRef.current = false;
      console.error(e);
      trackSurvey(SUBMIT_FAIL_EVENT, { id: eventId }, surveyData);
      setLoader({
        text: t('common.error.server'),
        type: 'error',
      });
    }
  };

  if (loader) {
    return (
      <SurveyLoader
        text={loader.text}
        type={loader.type}
        handleBack={() => setLoader(null)}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <header className="bg-background">
        <div className="flex justify-between p-4 items-center">
          <h1 className="font-light text-xl">
            {surveyData.patientDisplayName}
          </h1>
          <Button onPress={handleExit} variant="faded">
            {t('common.action.close')}
          </Button>
        </div>
      </header>
      <main className="flex flex-1 justify-center">
        <div className="flex flex-col w-full max-w-[800px] text-left px-12 py-4 space-y-4">
          {questions.map((question, i) => (
            <>
              <h2 className="font-semibold text-lg">{question.title}</h2>
              <h3>{question.subtitle}</h3>
              {errorFields.includes(i) && (
                <p className="text-danger-500">
                  {t('common.error.blank_input')}
                </p>
              )}
              <QuestionType
                answers={answers}
                getAnswer={(answer) => getAnswer(answer, i)}
                index={i}
                medicineHistory={medicineHistory}
                questions={questions}
              />
            </>
          ))}
          {surveyData.metricTracker && (
            <>
              {(errorFields.includes('date') ||
                errorFields.includes('time')) && (
                <p className="text-warning-500">
                  {t('surveyflat.error.datetime')}
                </p>
              )}
              <Card className="max-w-md">
                <CardBody>
                  <DatePicker
                    defaultValue={parseDate(
                      dayjs(date).format('YYYY-MM-DD').toString()
                    )}
                    isInvalid={errorFields.includes('date')}
                    label={t('common.input.date')}
                    onChange={(date) => setDate(_.toString(date))}
                    variant="underlined"
                  />
                </CardBody>
              </Card>
              <Card className="max-w-md">
                <CardBody>
                  <TimeInput
                    defaultValue={new Time(hour, minute)}
                    isInvalid={errorFields.includes('time')}
                    label={t('common.text.time')}
                    onChange={(val) => {
                      if (val) {
                        setHour(val.hour);
                        setMinute(val.minute);
                      }
                    }}
                    variant="underlined"
                  />
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </main>
      <footer className="flex justify-center p-4 space-x-4">
        <Button
          className="w-1/2 md:w-1/4"
          color="primary"
          onPress={handleSubmit}
          size="lg"
        >
          {t('common.action.submit')}
        </Button>
      </footer>
    </div>
  );
}
